/**
 * Hours Converter Calculator Dialog
 * Standalone helper: convert between decimal hours and HH:MM, and add/subtract in both formats.
 * Does not persist any values; user copies result into the form.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

import { MAX_HHMM_HOUR_COUNT } from '../../constants/hhmmHoursLimits';

// Parse HH:MM to decimal hours (e.g. "1:30" -> 1.5)
function parseHHMMToDecimal(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex === -1) return null;
  const h = parseInt(trimmed.substring(0, colonIndex), 10);
  const m = parseInt(trimmed.substring(colonIndex + 1), 10);
  if (isNaN(h) || isNaN(m)) return null;
  if (m < 0 || m > 59) return null;
  return h + m / 60;
}

// Parse decimal string to number (e.g. "1.5" -> 1.5)
function parseDecimal(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  return isNaN(n) ? null : n;
}

// Decimal to HH:MM string (hours bounded by MAX_HHMM_HOUR_COUNT, minutes 0-59)
function decimalToHHMM(decimal) {
  if (decimal == null || isNaN(decimal)) return '';
  const totalMinutes = Math.round(decimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = Math.abs(totalMinutes % 60);
  const hours = Math.min(MAX_HHMM_HOUR_COUNT, Math.max(-MAX_HHMM_HOUR_COUNT, h));
  const mins = m;
  if (hours < 0) return `${hours}:${String(mins).padStart(2, '0')}`;
  return `${hours}:${String(mins).padStart(2, '0')}`;
}

const HoursConverterCalculatorDialog = ({ open, onClose }) => {
  const [convertInput, setConvertInput] = useState('');
  const [convertMode, setConvertMode] = useState('decimalToHHMM'); // 'decimalToHHMM' | 'hhmmToDecimal'

  const [addSubOp1, setAddSubOp1] = useState('');
  const [addSubOp2, setAddSubOp2] = useState('');
  const [addSubOperator, setAddSubOperator] = useState('+');
  const [addSubFormat, setAddSubFormat] = useState('HH:MM'); // 'HH:MM' | 'decimal'

  const convertResult = useMemo(() => {
    if (!convertInput.trim()) return '';
    if (convertMode === 'decimalToHHMM') {
      const n = parseDecimal(convertInput);
      return n != null ? decimalToHHMM(n) : '';
    }
    const n = parseHHMMToDecimal(convertInput);
    return n != null ? String(Math.round(n * 100) / 100) : '';
  }, [convertInput, convertMode]);

  const addSubResult = useMemo(() => {
    let a = null;
    let b = null;
    if (addSubFormat === 'HH:MM') {
      a = parseHHMMToDecimal(addSubOp1);
      b = parseHHMMToDecimal(addSubOp2);
    } else {
      a = parseDecimal(addSubOp1);
      b = parseDecimal(addSubOp2);
    }
    if (a == null || b == null) return { decimal: '', hhmm: '' };
    const decimalRes = addSubOperator === '+' ? a + b : a - b;
    return {
      decimal: String(Math.round(decimalRes * 100) / 100),
      hhmm: decimalToHHMM(decimalRes)
    };
  }, [addSubOp1, addSubOp2, addSubOperator, addSubFormat]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Hours converter (decimal ⇄ HH:MM)</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          {/* Convert section */}
          <Typography variant="subtitle2" color="text.secondary">
            Convert
          </Typography>
          <ToggleButtonGroup
            value={convertMode}
            exclusive
            onChange={(_, v) => v != null && setConvertMode(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="decimalToHHMM">Decimal → HH:MM</ToggleButton>
            <ToggleButton value="hhmmToDecimal">HH:MM → Decimal</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            fullWidth
            label={convertMode === 'decimalToHHMM' ? 'Decimal (e.g. 1.5)' : 'HH:MM (e.g. 1:30)'}
            value={convertInput}
            onChange={(e) => setConvertInput(e.target.value)}
            placeholder={convertMode === 'decimalToHHMM' ? '1.5' : '1:30'}
          />
          {convertResult !== '' && (
            <Typography variant="body2">
              → <strong>{convertResult}</strong>
            </Typography>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Add or subtract
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Input format</InputLabel>
            <Select
              value={addSubFormat}
              label="Input format"
              onChange={(e) => setAddSubFormat(e.target.value)}
            >
              <MenuItem value="HH:MM">HH:MM</MenuItem>
              <MenuItem value="decimal">Decimal</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 80 }}
              placeholder={addSubFormat === 'HH:MM' ? '1:30' : '1.5'}
              value={addSubOp1}
              onChange={(e) => setAddSubOp1(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 56 }}>
              <Select
                value={addSubOperator}
                onChange={(e) => setAddSubOperator(e.target.value)}
                sx={{ py: 0.5 }}
              >
                <MenuItem value="+">+</MenuItem>
                <MenuItem value="-">−</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 80 }}
              placeholder={addSubFormat === 'HH:MM' ? '0:45' : '0.75'}
              value={addSubOp2}
              onChange={(e) => setAddSubOp2(e.target.value)}
            />
          </Box>
          {(addSubResult.decimal !== '' || addSubResult.hhmm !== '') && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                Result: <strong>{addSubResult.hhmm}</strong> ({addSubResult.decimal} decimal)
              </Typography>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Use this to convert or calculate; then type the result into Limit, Elapsed, or Value at Installation.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HoursConverterCalculatorDialog;
